interface Page {
  id: string;
  name: string;
}

interface Component {
  id: string;
  name: string;
  pageId: string;
}

interface Question {
  id: string;
  question: string;
  openText: string;
  inputType: 'text' | 'dropdown';
  dropdownOptions?: string[];
}

interface ComponentWithQuestions {
  id: string;
  name: string;
  questions: Question[];
}

export function getPageNames(): Page[] {
  return [
    { id: 'employer_profile', name: 'Employer Profile' },
    { id: 'messages', name: 'Messages' },
    { id: 'candidates', name: 'Candidates' },
    { id: 'employer_challenges', name: 'Employer Challenges' },
    { id: 'search_results', name: 'Search Results' },
    { id: 'public_user_profile', name: 'Public User Profile' },
    { id: 'employer_analytics', name: 'Employer Analytics' },
    { id: 'employer_subscription_tier', name: 'Employer Subscription Tier' },
    { id: 'settings', name: 'Settings' },
    { id: 'terms_and_conditions', name: 'Terms and Conditions' }
  ];
}

export function getComponentsForPages(selectedPageIds: string[]): { pageId: string; pageName: string; components: Component[] }[] {
  const pageComponents: Record<string, Component[]> = {
    employer_profile: [
      { id: 'edit_company_profile', name: 'Edit company profile', pageId: 'employer_profile' },
      { id: 'edit_my_profile', name: 'Edit my profile', pageId: 'employer_profile' },
      { id: 'manage_team', name: 'Manage team', pageId: 'employer_profile' },
      { id: 'tier_selection_modal', name: 'Tier selection modal', pageId: 'employer_profile' },
      { id: 'manage_team_modal', name: 'Manage team modal', pageId: 'employer_profile' },
      { id: 'public_link_toggle', name: 'Public link toggle', pageId: 'employer_profile' },
      { id: 'add_edit_company_culture_modal', name: 'Add/edit company culture modal', pageId: 'employer_profile' },
      { id: 'company_culture_display', name: 'Company culture display', pageId: 'employer_profile' },
      { id: 'add_edit_job_modal', name: 'Add/edit job modal', pageId: 'employer_profile' },
      { id: 'see_all_jobs_modal', name: 'See all jobs modal', pageId: 'employer_profile' },
      { id: 'add_edit_interview_process_modal', name: 'Add/edit interview process modal', pageId: 'employer_profile' },
      { id: 'interview_process_display', name: 'Interview process display', pageId: 'employer_profile' },
      { id: 'candidate_notes', name: 'Candidate notes', pageId: 'employer_profile' }
    ],
    messages: [
      { id: 'message_functionality', name: 'Message functionality', pageId: 'messages' },
      { id: 'attachments', name: 'Attachments', pageId: 'messages' },
      { id: 'share_challenge_modal', name: 'Share challenge modal', pageId: 'messages' },
      { id: 'add_to_watchlist_button', name: 'Add to watchlist button', pageId: 'messages' },
      { id: 'end_chat', name: 'End chat', pageId: 'messages' },
      { id: 'report_user', name: 'Report user', pageId: 'messages' },
      { id: 'vertical_sidebar', name: 'Vertical sidebar', pageId: 'messages' },
      { id: 'create_new_conversation_modal', name: 'Create new conversation modal', pageId: 'messages' }
    ],
    candidates: [
      { id: 'my_job_postings_tab', name: 'My job postings tab', pageId: 'candidates' },
      { id: 'sort_by_filter', name: 'Sort by filter', pageId: 'candidates' },
      { id: 'approve_reject_candidate_buttons', name: 'Approve/reject candidate buttons', pageId: 'candidates' },
      { id: 'shared_with_me_tab', name: 'Shared with me tab', pageId: 'candidates' },
      { id: 'share_with_team_modal', name: 'Share with team modal', pageId: 'candidates' }
    ],
    employer_challenges: [
      { id: 'my_challenges_tab', name: 'My challenges tab', pageId: 'employer_challenges' },
      { id: 'submissions_tab', name: 'Submissions tab', pageId: 'employer_challenges' },
      { id: 'share_challenge_modal_challenges', name: 'Share challenge modal', pageId: 'employer_challenges' },
      { id: 'add_edit_challenge', name: 'Add/edit challenge', pageId: 'employer_challenges' },
      { id: 'employer_challenge_showcase', name: 'Employer challenge showcase', pageId: 'employer_challenges' }
    ],
    search_results: [
      { id: 'candidate_card', name: 'Candidate card', pageId: 'search_results' },
      { id: 'jobdetailsmodal', name: 'Job details modal', pageId: 'search_results' }
    ],
    public_user_profile: [
      { id: 'header', name: 'Header', pageId: 'public_user_profile' },
      { id: 'skills_expertise', name: 'Skills & expertise', pageId: 'public_user_profile' },
      { id: 'featured_projects', name: 'Featured projects', pageId: 'public_user_profile' },
      { id: 'system_design_interview', name: 'System design interview', pageId: 'public_user_profile' },
      { id: 'completed_challenges', name: 'Completed challenges', pageId: 'public_user_profile' }
    ],
    employer_analytics: [
      { id: 'total_monthly_profile_views', name: 'Total / monthly profile views / unique viewers / internal vs external cards', pageId: 'employer_analytics' },
      { id: 'company_profile_trends_chart', name: 'Company profile trends chart', pageId: 'employer_analytics' },
      { id: 'recent_profile_visitors', name: 'Recent profile visitors', pageId: 'employer_analytics' },
      { id: 'job_posting_performance', name: 'Job posting performance', pageId: 'employer_analytics' },
      { id: 'applicant_quality_metrics', name: 'Applicant quality metrics', pageId: 'employer_analytics' },
      { id: 'company_wide_matches', name: 'Company wide matches', pageId: 'employer_analytics' },
      { id: 'applicant_response_rate', name: 'Applicant response rate', pageId: 'employer_analytics' },
      { id: 'avg_time_first_match', name: 'Avg time until a job receives its first match', pageId: 'employer_analytics' }
    ],
    employer_subscription_tier: [
      { id: 'subscription_tier_page', name: 'Subscription tier page', pageId: 'employer_subscription_tier' },
      { id: 'stripe_checkout', name: 'Stripe checkout', pageId: 'employer_subscription_tier' }
    ],
    settings: [],
    terms_and_conditions: []
  };

  const pageNameMap: Record<string, string> = {
    employer_profile: 'Employer Profile',
    messages: 'Messages',
    candidates: 'Candidates',
    employer_challenges: 'Employer Challenges',
    search_results: 'Search Results',
    public_user_profile: 'Public User Profile',
    employer_analytics: 'Employer Analytics',
    employer_subscription_tier: 'Employer Subscription Tier',
    settings: 'Settings',
    terms_and_conditions: 'Terms and Conditions'
  };

  return selectedPageIds.map(pageId => ({
    pageId,
    pageName: pageNameMap[pageId] || pageId,
    components: pageComponents[pageId] || []
  }));
}

export function getQuestionsForComponents(selectedComponentIds: string[]): ComponentWithQuestions[] {
  const allQuestions: Record<string, ComponentWithQuestions> = {
    edit_company_profile: {
      id: 'edit_company_profile',
      name: 'Edit company profile',
      questions: [
        {
          id: 'edit_company_profile_q1',
          question: 'Was there anything you expected to be able to define about your company that was missing?',
          openText: 'I was looking for a place to add ___ but couldn\'t find it.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I was looking for a place to add our mission statement and core values, but couldn\'t find it. The fields covered everything I needed to create a complete company profile.',
            'I was looking for a place to add specific office locations or designated remote regions, but couldn\'t find it.'
          ]
        }
      ]
    },
    edit_my_profile: {
      id: 'edit_my_profile',
      name: 'Edit my profile',
      questions: [
        {
          id: 'edit_my_profile_q1',
          question: 'Was it clear what the difference was between your personal profile and the company profile?',
          openText: 'I thought my profile was for ___, but it was actually for ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I thought my profile was for my public-facing role at the company, and it was, which was clear.',
            'The distinction was a bit confusing; I wasn\'t sure if my title and links would show up on the company page or just for me. I thought my profile was for my personal job-seeking efforts, but it was actually for representing my company.'
          ]
        }
      ]
    },
    manage_team_modal: {
      id: 'manage_team_modal',
      name: 'Manage team modal',
      questions: [
        {
          id: 'manage_team_modal_q1',
          question: 'When managing roles and permissions, did the options feel comprehensive enough for your team\'s structure, or did you need more granular control?',
          openText: 'I wanted to give a recruiter permission to ___, but I could only make them a full admin.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The admin and recruiter roles were exactly what I needed and the distinction was clear.',
            'I wanted to create a "Hiring Manager" role with limited permissions, but I couldn\'t.'
          ]
        }
      ]
    },
    tier_selection_modal: {
      id: 'tier_selection_modal',
      name: 'Tier selection modal',
      questions: [
        {
          id: 'tier_selection_modal_q1',
          question: 'Did the different company tiers (e.g., Core, Hiring Kernel, Flagship) feel like they offered meaningful, distinct value? Was it clear what you got with each?',
          openText: 'The tiers felt ___. I chose ___ because ___, but I was still unclear about ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The tiers felt distinct.',
            'The tiers felt a bit confusing.'
          ]
        }
      ]
    },
    public_link_toggle: {
      id: 'public_link_toggle',
      name: 'Public link toggle',
      questions: [
        {
          id: 'public_link_toggle_q1',
          question: 'Was it clear what would happen when you enabled the public link? Did you feel confident that the right information would be shared?',
          openText: 'I thought turning it on would ___, but I was worried it might also show ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I thought turning it on would make my company profile visible at a shareable URL, and it did exactly that.',
            'I thought turning it on would publish all of our draft job postings, so I was hesitant to touch it.'
          ]
        }
      ]
    },
    add_edit_company_culture_modal: {
      id: 'add_edit_company_culture_modal',
      name: 'Add/edit company culture modal',
      questions: [
        {
          id: 'add_edit_company_culture_modal_q1',
          question: 'When filling out the company culture details, did the sections feel relevant and comprehensive for describing your engineering environment?',
          openText: 'The sections were mostly ___. I wanted to describe our ___ but there wasn\'t a good place for it.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The sections were mostly relevant.',
            'I wanted to describe our unique approach to mentorship and junior developer growth, but there wasn\'t a good place for it.'
          ]
        }
      ]
    },
    company_culture_display: {
      id: 'company_culture_display',
      name: 'Company culture display',
      questions: [
        {
          id: 'company_culture_display_q1',
          question: 'Looking at how your culture is presented, what information do you think is most impactful for a jobseeker?',
          openText: 'I think a candidate would care most about ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I think a candidate would care most about the "Team Values" and "Career Growth" sections.',
            'I think a candidate would care most about our benefits and remote work policy.'
          ]
        }
      ]
    },
    add_edit_job_modal: {
      id: 'add_edit_job_modal',
      name: 'Add/edit job modal',
      questions: [
        {
          id: 'add_edit_job_modal_q1',
          question: 'The "Ideal Candidate" section lets you set score weightings and requirements. Did this feel like a powerful tool for sourcing, or was it too complex and time-consuming?',
          openText: 'Using the matching algorithm sliders felt ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Using the matching algorithm sliders felt powerful because I could precisely define what kind of candidate I was looking for',
            'Using the matching algorithm sliders felt too complex because I wasn\'t sure how my adjustments would actually impact the candidate recommendations.',
            'It was too time-consuming; I\'d rather just write my requirements in the description and review everyone manually.'
          ]
        }
      ]
    },
    see_all_jobs_modal: {
      id: 'see_all_jobs_modal',
      name: 'See all jobs modal',
      questions: [
        {
          id: 'see_all_jobs_modal_q1',
          question: 'When viewing all your jobs, what is the most important piece of information you look for at a glance? Is there anything missing that would help you manage your postings more effectively?',
          openText: 'At a glance, I\'m trying to see ___. I wish the view also included ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'At a glance, I\'m trying to see which jobs are featured and which are getting the most applicants.',
            'I wish the view also included the name of the recruiter assigned to each job right on the card.'
          ]
        }
      ]
    },
    add_edit_interview_process_modal: {
      id: 'add_edit_interview_process_modal',
      name: 'Add/edit interview process modal',
      questions: [
        {
          id: 'add_edit_interview_process_modal_q1',
          question: 'Did you feel that the different sections (Overview, Technical, Behavioral, etc.) allowed you to accurately document how your team interviews candidates?',
          openText: 'Documenting our process was ___. I struggled with the ___ section because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Documenting our process was straightforward. I struggled with the Technical Assessment section because we use a mix of live coding and take-home projects and it was hard to specify when each occurs. The sections were perfect and covered all the details we wanted to share with candidates to set clear expectations.',
            'Documenting our process was difficult. I struggled with the Interview Stages section because our process isn\'t a fixed number of steps; it changes based on the candidate.'
          ]
        }
      ]
    },
    interview_process_display: {
      id: 'interview_process_display',
      name: 'Interview process display',
      questions: [
        {
          id: 'interview_process_display_q1',
          question: 'Does this presentation format make your interview process look clear and fair to candidates, or does it look intimidating or confusing?',
          openText: 'I think this format makes our process look ___, but I worry that ___ might scare some people away.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I think this format makes our process look very transparent, but I worry that listing five separate stages might scare some people away before they even apply.',
            'This format is excellent; it clearly communicates what to expect, which I believe will attract the right kind of candidates.',
            'I worry that the "Technical Assessment" details look too intense and might discourage self-taught developers from applying.'
          ]
        }
      ]
    },
    candidate_notes: {
      id: 'candidate_notes',
      name: 'Candidate notes',
      questions: [
        {
          id: 'candidate_notes_q1',
          question: 'When you use the Candidate Notes view, what is the most critical task you\'re trying to accomplish? Does the layout help or hinder that task?',
          openText: 'My main goal here is to ___, and the layout makes it ___ to do that.',
          inputType: 'dropdown',
          dropdownOptions: [
            'My main goal here is to quickly review my team\'s feedback on a candidate before an interview, and the layout makes it easy to do that by separating ratings and notes.',
            'My main goal here is to see all recent activity for all candidates, but I have to click into each candidate individually, which is slow.',
            'My main goal is to add a quick note, and the layout makes it simple to do that without getting distracted by other information.'
          ]
        }
      ]
    },
    message_functionality: {
      id: 'message_functionality',
      name: 'Message functionality',
      questions: [
        {
          id: 'message_functionality_q1',
          question: 'How intuitive did you find the core messaging actions like replying to a specific message or editing your own message after sending?',
          openText: 'The messaging experience felt ___. I found the ___ feature to be particularly ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The messaging experience felt very modern and intuitive.',
            'The messaging experience felt a bit clunky.'
          ]
        }
      ]
    },
    attachments: {
      id: 'attachments',
      name: 'Attachments',
      questions: [
        {
          id: 'attachments_q1',
          question: 'When sending or receiving attachments (like resumes or design files), was the process clear and did the previews meet your expectations?',
          openText: 'Attaching a file was ___. When I received a file, I expected to be able to ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Attaching a file was simple and worked as expected.',
            'Attaching a file was easy, but I couldn\'t attach more than one at a time which was frustrating. I expected to be able to drag and drop multiple files at once.'
          ]
        }
      ]
    },
    share_challenge_modal: {
      id: 'share_challenge_modal',
      name: 'Share challenge modal',
      questions: [
        {
          id: 'share_challenge_modal_q1',
          question: 'Did sharing a technical challenge from within a conversation feel like a natural next step in your workflow, or would you expect to do this elsewhere?',
          openText: 'The \'Share Challenge\' feature felt ___ in this context because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The \'Share Challenge\' feature felt perfectly placed.',
            'The \'Share Challenge\' feature felt out of place. I usually send challenges from a separate dashboard after a formal screening call, not in the middle of an informal chat.'
          ]
        }
      ]
    },
    add_to_watchlist_button: {
      id: 'add_to_watchlist_button',
      name: 'Add to watchlist button',
      questions: [
        {
          id: 'add_to_watchlist_button_q1',
          question: 'Was the purpose of the "Add to Watchlist" button immediately clear? What kind of candidate would you use this for?',
          openText: 'I would use the watchlist for candidates who ___, but I was initially unsure if ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'I would use the watchlist for candidates who are promising but maybe not a fit for the current role.',
            'I was initially unsure if adding them to a watchlist would notify them or if it was a private action for my team. The lack of confirmation made me hesitant to click it.'
          ]
        }
      ]
    },
    end_chat: {
      id: 'end_chat',
      name: 'End chat',
      questions: [
        {
          id: 'end_chat_q1',
          question: 'In what situation would you use the "End Chat" function for a group conversation? How is that different from just leaving the group?',
          openText: 'To me, \'End Chat\' implies ___, and I would use it when ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'To me, \'End Chat\' implies a formal closing for everyone. I would use it when an interview panel debrief is officially concluded and no more messages are needed.',
            'To me, \'End Chat\' is confusing. It sounds too permanent. I would rather just leave the group and let the conversation archive itself naturally.'
          ]
        }
      ]
    },
    report_user: {
      id: 'report_user',
      name: 'Report user',
      questions: [
        {
          id: 'report_user_q1',
          question: 'Do the reasons for reporting a user feel appropriate and sufficient for interactions in a professional hiring context?',
          openText: 'The reporting options seem to cover ___, but they might be missing ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The reporting options seem to cover all the serious issues like spam and harassment. It gives me confidence that the platform takes safety seriously.',
            'The reporting options are for extreme cases. They might be missing a way to flag a candidate who is simply unprofessional or unresponsive, which isn\'t harassment but is still valuable feedback for the platform.'
          ]
        }
      ]
    },
    vertical_sidebar: {
      id: 'vertical_sidebar',
      name: 'Vertical sidebar',
      questions: [
        {
          id: 'vertical_sidebar_q1',
          question: 'Look at a single conversation in your chat list. Does the information shown (name, preview, unread count) give you enough context to prioritize which message to open next?',
          openText: 'The sidebar gives me ___ context. To make it more useful, I wish it also showed ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The sidebar gives me enough context. The bold name for unread messages and the message preview is all I need to quickly scan and respond.',
            'The sidebar gives me almost enough context. To make it more useful, I wish it also showed the candidate\'s associated job posting, since I might be talking to multiple candidates for different roles.'
          ]
        }
      ]
    },
    create_new_conversation_modal: {
      id: 'create_new_conversation_modal',
      name: 'Create new conversation modal',
      questions: [
        {
          id: 'create_new_conversation_modal_q1',
          question: 'When creating a new conversation, did the list of suggested users (team members, matched candidates) make sense and help you start a chat faster?',
          openText: 'The list of suggested users was ___. I was looking for ___ and it was ___ to find them.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The list of suggested users was very helpful. I was looking for my team members to start a group chat about a candidate, and they were all right there and easy to select.',
            'The list of suggested users was confusing. It mixed my teammates with all matched candidates, and the search didn\'t seem to differentiate between them, making it hard to find a specific person.'
          ]
        }
      ]
    },
    my_job_postings_tab: {
      id: 'my_job_postings_tab',
      name: 'My job postings tab',
      questions: [
        {
          id: 'my_job_postings_tab_q1',
          question: 'How useful is the separation between candidates who applied to \'My Postings\' versus those in the \'Shared with Me\' tab for organizing your workflow?',
          openText: 'This separation was ___ for my process because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This separation was crucial for my process because it let me focus only on candidates for my own roles, without getting distracted by candidates my teammates were handling.',
            'This separation was confusing for my process because candidates for the same job would appear in different tabs depending on who they were shared with, and I\'d prefer to see everyone for a specific job in one place.'
          ]
        }
      ]
    },
    sort_by_filter: {
      id: 'sort_by_filter',
      name: 'Sort by filter',
      questions: [
        {
          id: 'sort_by_filter_q1',
          question: 'When reviewing a list of candidates, how valuable were the sorting options (\'Most Recent\', \'System Design Score\', \'Challenges\') in helping you identify the strongest applicants first?',
          openText: 'The sorting options were ___. I found sorting by ___ to be the most useful for ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The sorting options were extremely valuable. I found sorting by \'System Design Score\' to be the most useful for instantly bubbling up the technically strongest candidates to the top, saving me a lot of time.',
            'The sorting options were somewhat helpful, but I wish I could sort by years of experience or a keyword in their bio, as the current options are too focused on platform-specific metrics.'
          ]
        }
      ]
    },
    approve_reject_candidate_buttons: {
      id: 'approve_reject_candidate_buttons',
      name: 'Approve/reject candidate buttons',
      questions: [
        {
          id: 'approve_reject_candidate_buttons_q1',
          question: 'After reviewing a candidate, what did you expect to happen when you clicked the \'Approve\' (check) or \'Reject\' (X) buttons? Was the outcome clear and did it match your expectation?',
          openText: 'When I clicked \'Approve\', I expected ___, and what actually happened was ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'When I clicked \'Approve\', I expected it to notify the candidate and open a direct message channel, and that\'s exactly what happened. It was a seamless transition from reviewing to engaging.',
            'When I clicked \'Reject\', I expected the candidate to just be removed from my list, but I wasn\'t sure if they would receive a rejection email automatically. The lack of a confirmation step made me nervous to use it.'
          ]
        }
      ]
    },
    shared_with_me_tab: {
      id: 'shared_with_me_tab',
      name: 'Shared with me tab',
      questions: [
        {
          id: 'shared_with_me_tab_q1',
          question: 'When you viewed candidates shared by your team, was it clear why they were shared and what action you needed to take?',
          openText: 'The \'Shared with Me\' tab was ___. It was clear/unclear who shared the candidate because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The \'Shared with Me\' tab was very helpful. It was clear who shared the candidate list and for which job, making it easy for me to jump in and assist with the hiring process.',
            'The \'Shared with Me\' tab was a bit of a black box. It was unclear if the person who shared it wanted me to review, interview, or just be aware of the candidates. I wish they could add a quick note with the share.'
          ]
        }
      ]
    },
    share_with_team_modal: {
      id: 'share_with_team_modal',
      name: 'Share with team modal',
      questions: [
        {
          id: 'share_with_team_modal_q1',
          question: 'Think about the process of sharing candidates for a specific job with your team members. Did this modal make it easy to select the right people and get their input?',
          openText: 'Sharing candidates with my team was ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Sharing candidates with my team was very easy because the modal showed me a clear list of my colleagues, and I could quickly select who needed to see the applicant list.',
            'Sharing candidates with my team was a bit confusing because I had to do it on a per-job basis. I was hoping for a way to share a single interesting candidate with a colleague without tying them to a specific job posting.'
          ]
        }
      ]
    },
    my_challenges_tab: {
      id: 'my_challenges_tab',
      name: 'My challenges tab',
      questions: [
        {
          id: 'my_challenges_tab_q1',
          question: 'Looking at your list of custom challenges, does this view give you a clear and actionable overview of the assessments you\'ve created?',
          openText: 'This view is ___ for managing my challenges because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This view is great for managing my challenges because I can quickly see which are drafts vs. published, and the filters help me find specific ones easily.',
            'This view is missing key information. I wish I could see at a glance how many candidates have been invited to each challenge without having to go to the submissions tab.'
          ]
        }
      ]
    },
    submissions_tab: {
      id: 'submissions_tab',
      name: 'Submissions tab',
      questions: [
        {
          id: 'submissions_tab_q1',
          question: 'When reviewing the list of candidate submissions, how effective is the grouping by challenge for your workflow? Does it help you compare candidates fairly?',
          openText: 'Grouping submissions by challenge is ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Grouping submissions by challenge is extremely effective because it lets me focus on one assessment at a time and compare multiple candidates\' approaches to the exact same problem.',
            'Grouping submissions by challenge is not ideal for me. I prefer to review all submissions from a single candidate at once to get a holistic view of their skills, but here I have to find them under each separate challenge.'
          ]
        }
      ]
    },
    share_challenge_modal_challenges: {
      id: 'share_challenge_modal_challenges',
      name: 'Share challenge modal',
      questions: [
        {
          id: 'share_challenge_modal_challenges_q1',
          question: 'When you decide to send a challenge to a candidate, how intuitive is the process of selecting the challenge, the candidates, and setting an expiration date?',
          openText: 'The process of sending a challenge felt ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The process of sending a challenge felt very streamlined because the modal let me quickly find the right challenge, pick from my list of interested candidates, and set an expiration date all in one place.',
            'The process of sending a challenge felt a bit disconnected. I couldn\'t easily see which challenges a candidate had already been invited to, so I was worried about sending duplicates.'
          ]
        }
      ]
    },
    add_edit_challenge: {
      id: 'add_edit_challenge',
      name: 'Add/edit challenge',
      questions: [
        {
          id: 'add_edit_challenge_q1',
          question: 'When creating or editing a challenge, did the form fields (context, instructions, schema) give you enough power and flexibility to represent a real-world problem from your company?',
          openText: 'Creating a challenge was ___ because the form ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Creating a challenge was very empowering because the form allowed me to provide detailed context and even a JSON schema, which makes the task much more realistic than a generic algorithm problem.',
            'Creating a challenge was difficult because the form is very rigid. I wanted to upload a starting file or a small project for the candidate to work with, but I could only provide text and JSON.'
          ]
        }
      ]
    },
    employer_challenge_showcase: {
      id: 'employer_challenge_showcase',
      name: 'Employer challenge showcase',
      questions: [
        {
          id: 'employer_challenge_showcase_q1',
          question: 'When reviewing a candidate\'s submission video and audio, how well did this player help you understand their thought process and technical skills?',
          openText: 'The review player was ___ for evaluating the candidate because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The review player was fantastic for evaluating the candidate because watching their screen while hearing their thought process gave me a much deeper insight than just looking at the final code.',
            'The review player was difficult to use because I couldn\'t easily skip to different parts of the video or control the playback speed, which made reviewing long videos tedious.'
          ]
        }
      ]
    },
    candidate_card: {
      id: 'candidate_card',
      name: 'Candidate card',
      questions: [
        {
          id: 'candidate_card_q1',
          question: 'When you see a candidate card in the search results, what information is most critical for deciding if they are a potential match? Does the card give you this information effectively?',
          openText: 'The most important piece of information on the card for me is ___. The card is/is not effective because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The most important piece of information on the card for me is their System Design score and top skills. The card is very effective because it surfaces these key technical signals immediately, so I don\'t have to waste time clicking into profiles that aren\'t a fit.',
            'The most important information for me is their years of experience, but the card doesn\'t show that. The card is not very effective because it\'s too focused on platform metrics like challenges, which don\'t tell me about their real-world impact.'
          ]
        }
      ]
    },
    jobdetailsmodal: {
      id: 'jobdetailsmodal',
      name: 'Job details modal',
      questions: [
        {
          id: 'jobdetailsmodal_q1',
          question: 'When viewing the job details, was it easy to understand the key requirements and what made the opportunity unique?',
          openText: 'The layout of the job details was ___. It was easy/hard to find ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The layout of the job details was very clear. It was easy to find the core responsibilities and the required technical skills because they were in distinct, well-labeled sections.',
            'The layout of the job details was a bit confusing. It was hard to find the salary range and benefits information because it was mixed in with a lot of other details at the bottom.'
          ]
        }
      ]
    },
    header: {
      id: 'header',
      name: 'Header',
      questions: [
        {
          id: 'header_q1',
          question: 'When you first land on a candidate\'s profile, how well does the header section (name, title, bio, tier) summarize who they are and whether you should keep scrolling?',
          openText: 'The header gave me ___ of an impression because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The header gave me an excellent first impression because the title and bio immediately told me their specialty, and the tier badge gave me a quick signal of their platform engagement.',
            'The header gave me a weak impression.'
          ]
        }
      ]
    },
    skills_expertise: {
      id: 'skills_expertise',
      name: 'Skills & expertise',
      questions: [
        {
          id: 'skills_expertise_q1',
          question: 'Does seeing a list of skills, as presented in the "Skills & Expertise" section, give you a meaningful signal about the candidate\'s capabilities, or does it feel like a self-reported list of keywords?',
          openText: 'This skills section feels ___ as a signal because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This skills section feels like a strong signal.',
            'This skills section feels like just another keyword list.'
          ]
        }
      ]
    },
    featured_projects: {
      id: 'featured_projects',
      name: 'Featured projects',
      questions: [
        {
          id: 'featured_projects_q1',
          question: 'When you view the "Featured Projects," does the immersive showcase (video demo, description, tech stack) give you a clear understanding of what the candidate built and their role in it?',
          openText: 'The project showcase ___ at conveying their ability because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The project showcase did an excellent job of conveying their ability because watching the video demo was like getting a personal tour of their work, which is far more insightful than just reading about it.',
            'The project showcase fell short of conveying their ability because while the demo was nice, there was no clear section for them to explain the problems they solved or the specific technical challenges they overcame.'
          ]
        }
      ]
    },
    system_design_interview: {
      id: 'system_design_interview',
      name: 'System design interview',
      questions: [
        {
          id: 'system_design_interview_q1',
          question: 'As an evaluation tool, how does watching this pre-recorded System Design Interview compare to a live interview? What information does it give you that a live one doesn\'t, and vice-versa?',
          openText: 'This format is ___ for evaluation because it shows me ___, but it misses ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This format is great for evaluation because it shows me the candidate\'s pure, uninterrupted thought process. I can see how they structure their thoughts without the pressure of a live back-and-forth.',
            'This format is a poor substitute for evaluation because it misses the most important part of a live interview: the ability to probe deeper on their decisions, ask follow-up questions, and see how they react to new constraints.'
          ]
        }
      ]
    },
    completed_challenges: {
      id: 'completed_challenges',
      name: 'Completed challenges',
      questions: [
        {
          id: 'completed_challenges_q1',
          question: 'We break down skills into three levels: Metaskills (e.g., "Frontend"), Subskills (e.g., "React"), and the specific Challenge they completed. Does this hierarchy give you a clear, powerful signal of expertise, or is it too noisy?',
          openText: 'The three-level skill breakdown feels ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The three-level skill breakdown feels very powerful because it allows me to see not just that they know \'React,\' but exactly how they\'ve applied it in a practical challenge, which is a much stronger signal.',
            'The three-level skill breakdown feels too noisy. I have to parse through too many layers to understand their core competencies. I\'d rather just see a high-level list of their strongest skills.'
          ]
        }
      ]
    },
    total_monthly_profile_views: {
      id: 'total_monthly_profile_views',
      name: 'Total / monthly profile views / unique viewers / internal vs external cards',
      questions: [
        {
          id: 'total_monthly_profile_views_q1',
          question: 'Do these four summary cards give you a quick, valuable snapshot of your profile\'s reach? Are these the most important numbers to see first?',
          openText: 'Seeing these top-level numbers felt ___. I was most interested in the ___ metric.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Seeing these top-level numbers felt very insightful. I was most interested in the Internal vs. External metric, as it clearly showed me how many of our views come from people actively searching the platform versus direct links.',
            'Seeing these top-level numbers felt like vanity metrics. I was most interested in seeing the number of applications we\'ve received, but I had to scroll down to find that information.'
          ]
        }
      ]
    },
    company_profile_trends_chart: {
      id: 'company_profile_trends_chart',
      name: 'Company profile trends chart',
      questions: [
        {
          id: 'company_profile_trends_chart_q1',
          question: 'Does this chart effectively tell a story about your profile\'s activity over time? Do the time range filters meet your needs?',
          openText: 'The trends chart was ___ at helping me understand our visibility because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The trends chart was great at helping me understand our visibility because I could clearly see a spike in views after we posted a new job, which confirms our strategy is working.',
            'The trends chart was hard to interpret because it only shows views. I wish I could overlay applications or matches on the same chart to see if view spikes actually lead to results.'
          ]
        }
      ]
    },
    recent_profile_visitors: {
      id: 'recent_profile_visitors',
      name: 'Recent profile visitors',
      questions: [
        {
          id: 'recent_profile_visitors_q1',
          question: 'Is seeing a list of recent visitors to your company profile an actionable tool for you? What would you do with this information?',
          openText: 'The \'Recent Visitors\' list is ___ for me because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The \'Recent Visitors\' list is very actionable for me because if I see a high-quality candidate has viewed our profile, I can proactively reach out to them instead of waiting for them to apply.',
            'The \'Recent Visitors\' list is not very useful for me because it feels a bit like a vanity metric. I don\'t have time to look through every visitor; I\'d rather focus on those who have already shown interest by applying.'
          ]
        }
      ]
    },
    job_posting_performance: {
      id: 'job_posting_performance',
      name: 'Job posting performance',
      questions: [
        {
          id: 'job_posting_performance_q1',
          question: 'Does the breakdown of Views, Applications, Matches, and Avg. Score for each job help you understand which postings are effective and which need improvement?',
          openText: 'This performance breakdown helps me diagnose my job postings ___. I wish it also included ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This performance breakdown helps me diagnose my job postings very well. I can immediately see which jobs have a low view-to-application conversion rate, which tells me I need to improve the job description.',
            'This performance breakdown is a good start, but it\'s missing key metrics. I wish it also included the average time a candidate spends in each stage of the pipeline for that specific job.'
          ]
        }
      ]
    },
    applicant_quality_metrics: {
      id: 'applicant_quality_metrics',
      name: 'Applicant quality metrics',
      questions: [
        {
          id: 'applicant_quality_metrics_q1',
          question: 'Do these aggregated metrics (Avg. Interview Score, Challenges, Matches) give you a meaningful overview of the quality of candidates you\'re attracting across all jobs?',
          openText: 'These \'quality\' metrics are ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'These \'quality\' metrics are insightful because they give me a high-level sense of our talent pool. Seeing a high average interview score across all applicants gives me confidence in our sourcing strategy.',
            'These \'quality\' metrics are a bit misleading because lumping all applicants together hides the fact that our senior roles attract high-quality candidates while our junior roles don\'t. I need to see this broken down by job.'
          ]
        }
      ]
    },
    company_wide_matches: {
      id: 'company_wide_matches',
      name: 'Company wide matches',
      questions: [
        {
          id: 'company_wide_matches_q1',
          question: 'How do you interpret the "Company-Wide Matches" number? Is this a key indicator of success for your team?',
          openText: 'This single number for total matches is ___ because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This single number for total matches is a great motivator for the team because it\'s a clear, simple KPI that shows the direct result of our efforts on the platform.',
            'This single number for total matches is not very useful without context because an \'all-time\' number doesn\'t tell me if our performance is improving or declining this month.'
          ]
        }
      ]
    },
    applicant_response_rate: {
      id: 'applicant_response_rate',
      name: 'Applicant response rate',
      questions: [
        {
          id: 'applicant_response_rate_q1',
          question: 'Does the "Applicant Response Rate" metric give you clear feedback on how effectively your team is engaging with candidates who reach out?',
          openText: 'This response rate metric is ___ for managing my team\'s performance because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'This response rate metric is critical for managing my team\'s performance because it provides a clear, objective number that I can use to ensure we\'re providing a good candidate experience.',
            'This response rate metric is hard to trust because it doesn\'t seem to distinguish between an initial message from a candidate and their subsequent replies. Our rate looks low, but it\'s because we don\'t reply to every "thanks" message.'
          ]
        }
      ]
    },
    avg_time_first_match: {
      id: 'avg_time_first_match',
      name: 'Avg time until a job receives its first match',
      questions: [
        {
          id: 'avg_time_first_match_q1',
          question: 'How valuable is it to see your average time to first match and compare it to the industry average?',
          openText: 'The \'time to first match\' metric is ___ for me because ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The \'time to first match\' metric is extremely valuable for me because seeing that we are faster than the industry average is a great data point to share with leadership about our hiring efficiency.',
            'The \'time to first match\' metric is interesting, but the \'industry average\' is too broad. I need to compare myself to companies of a similar size and stage, not the entire industry.'
          ]
        }
      ]
    },
    subscription_tier_page: {
      id: 'subscription_tier_page',
      name: 'Subscription tier page',
      questions: [
        {
          id: 'subscription_tier_page_q1',
          question: 'When comparing the subscription plans, was it immediately clear which plan was right for your company\'s needs? Did the feature list effectively communicate the value you get at each price point?',
          openText: 'Comparing the tiers, it was ___ to understand the differences. The feature that most convinced me to consider upgrading was ___.',
          inputType: 'dropdown',
          dropdownOptions: [
            'Comparing the tiers, it was very easy to understand the differences.',
            'Comparing the tiers, it was difficult to understand the real value.'
          ]
        }
      ]
    },
    stripe_checkout: {
      id: 'stripe_checkout',
      name: 'Stripe checkout',
      questions: [
        {
          id: 'stripe_checkout_q1',
          question: 'When you proceed to checkout, does the payment process feel secure and straightforward? Is there any information you expected to see on the checkout page that was missing?',
          openText: 'The checkout process felt ___. I was looking for ___ before entering my payment details.',
          inputType: 'dropdown',
          dropdownOptions: [
            'The checkout process felt very secure and professional. Seeing the Stripe branding and a clear summary of my selected plan and the total cost gave me the confidence to complete the purchase.',
            'The checkout process felt a bit abrupt. I was looking for a confirmation of the billing cycle (monthly vs. annual) and the exact renewal date before entering my payment details, but that wasn\'t shown.'
          ]
        }
      ]
    }
  };

  return selectedComponentIds
    .map(id => allQuestions[id])
    .filter(Boolean);
}